import * as React from "react";
import { type LucideIcon } from "lucide-react";
import { mergeClassNames } from "./class-names";

export interface SectionCardProps {
  readonly title: React.ReactNode;
  /** Heading level, so a page keeps one logical heading hierarchy. */
  readonly as?: "h2" | "h3";
  readonly icon?: LucideIcon;
  readonly description?: React.ReactNode;
  /** Record count shown beside the title, for skimmable list sections. */
  readonly count?: number;
  readonly actions?: React.ReactNode;
  readonly className?: string | undefined;
  /** Replaces the default body padding — pass `""` for edge-to-edge lists. */
  readonly bodyClassName?: string | undefined;
  readonly children: React.ReactNode;
}

/**
 * The standard framed region of a Hisani workspace: a titled header rail
 * carrying the section's icon, count, and one action group, over a body that
 * holds either padded content or an edge-to-edge record list.
 */
export function SectionCard({
  title,
  as: Heading = "h2",
  icon: Icon,
  description,
  count,
  actions,
  className,
  bodyClassName,
  children,
}: SectionCardProps) {
  return (
    <section
      data-slot="section-card"
      className={mergeClassNames(
        "min-w-0 overflow-hidden rounded-xl border border-border-subtle bg-surface",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b border-border-subtle px-5 py-4">
        <div
          className={mergeClassNames(
            "flex min-w-0 gap-3",
            description === undefined ? "items-center" : "items-start",
          )}
        >
          {Icon === undefined ? null : (
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-brand-subtle text-brand-ink">
              <Icon className="size-4" aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <Heading className="text-heading-small flex min-w-0 flex-wrap items-center gap-2 text-primary [overflow-wrap:anywhere]">
              {title}
              {count === undefined ? null : (
                <span className="text-body-small inline-flex min-w-6 items-center justify-center rounded-full bg-subtle px-2 py-0.5 font-semibold text-secondary">
                  {count}
                </span>
              )}
            </Heading>
            {description === undefined ? null : (
              <p className="text-body mt-0.5 text-secondary">{description}</p>
            )}
          </div>
        </div>
        {actions === undefined ? null : (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>
      <div className={bodyClassName ?? "p-5"}>{children}</div>
    </section>
  );
}

export interface EmptyStateProps {
  readonly icon: LucideIcon;
  readonly title: React.ReactNode;
  readonly description?: React.ReactNode;
  /** The most useful next action, when the viewer is allowed to take one. */
  readonly action?: React.ReactNode;
  readonly className?: string | undefined;
}

/** Explains why a region is empty and offers the next action, instead of a
 * bare line of grey text. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={mergeClassNames(
        "flex flex-col items-center gap-3 rounded-lg border border-dashed border-border-default bg-canvas px-6 py-10 text-center",
        className,
      )}
    >
      <span className="flex size-11 items-center justify-center rounded-full bg-brand-subtle text-brand-ink">
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <div className="grid gap-1">
        <p className="text-body font-semibold text-primary">{title}</p>
        {description === undefined ? null : (
          <p className="text-body mx-auto max-w-sm text-secondary">
            {description}
          </p>
        )}
      </div>
      {action === undefined ? null : <div className="mt-1">{action}</div>}
    </div>
  );
}
